import { useMemo } from "react";

import ProgressBar from "ds/components/ProgressBar";
import CollapsibleSection from "ds/components/CollapsibleSection";

import GetStartedChecklistFeatureTile from "./FeatureTile";
import LaunchPadGetStartedChecklistSkeleton from "./Skeleton";
import { LaunchPadFeatureMetadata, LaunchPadsFeatureProgress } from "../../types";

type LaunchPadChecklistLayoutProps<T extends string> = {
  onboardingProgressInitialLoading: boolean;
  features: LaunchPadFeatureMetadata<T>[];
  progressByFeature: LaunchPadsFeatureProgress<T>;
};

const LaunchPadChecklistLayout = <T extends string>({
  onboardingProgressInitialLoading,
  features,
  progressByFeature,
}: LaunchPadChecklistLayoutProps<T>) => {
  const completedFeaturesCount = useMemo(
    () => features.filter(({ feature }) => progressByFeature[feature].isCompleted).length,
    [features, progressByFeature]
  );

  return (
    <CollapsibleSection
      initialIsCollapsed={false}
      title="Get started checklist"
      ariaLevel={2}
      ariaLabel="Get started checklist"
      contentGap="large"
      headerSuffix={
        !onboardingProgressInitialLoading && (
          <ProgressBar
            ariaLabel="Number of completed features"
            max={features.length}
            current={completedFeaturesCount}
          />
        )
      }
    >
      {onboardingProgressInitialLoading ? (
        <LaunchPadGetStartedChecklistSkeleton features={features} />
      ) : (
        features.map(({ feature, title, url, icon, analyticsTitle }) => (
          <GetStartedChecklistFeatureTile
            key={feature}
            title={title}
            to={url}
            icon={icon}
            isCompleted={progressByFeature[feature].isCompleted}
            analyticsTitle={analyticsTitle}
          />
        ))
      )}
    </CollapsibleSection>
  );
};

export default LaunchPadChecklistLayout;
